import { useState } from "react";
import { useParams, useLocation, useSearch } from "wouter";
import { usePageTitle } from "@/hooks/use-page-title";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import {
  Heart,
  DollarSign,
  User,
  Mail,
  CreditCard,
  CheckCircle,
  ArrowLeft,
  Calendar,
  Loader2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormDescription,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";

const contributionSchema = z.object({
  amount: z.coerce.number().min(100, "Minimum contribution is $1.00"),
  contributorName: z.string().optional(),
  contributorEmail: z.string().email().optional().or(z.literal("")),
});

type ContributionFormValues = z.infer<typeof contributionSchema>;

interface PublicWallet {
  id: string;
  patientName: string;
  category: string;
  description?: string;
  goalAmount?: number;
  totalContributions: number;
  balance: number;
}

const presetAmounts = [500, 1000, 2500, 5000, 10000]; // in cents

function formatCents(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

function getCategoryLabel(category: string): string {
  const labels: Record<string, string> = {
    oncology: "Oncology Care",
    specialty_pharmacy: "Specialty Pharmacy",
    chronic_condition: "Chronic Condition",
    transplant: "Transplant Care",
    pediatric: "Pediatric Care",
    maternal: "Maternal Health",
    other: "Medical Care",
  };
  return labels[category] || category;
}

export default function WalletContributePage() {
  usePageTitle("Contribute to Health Wallet", "Make a contribution to a health wallet and support community healthcare funding.");
  const { token } = useParams<{ token: string }>();
  const [, setLocation] = useLocation();
  const search = useSearch();
  const { toast } = useToast();
  const [selectedAmount, setSelectedAmount] = useState<number | null>(null);
  const [contributionType, setContributionType] = useState<"one-time" | "monthly">("one-time");

  const searchParams = new URLSearchParams(search);
  const sessionId = searchParams.get("session_id");
  const isSuccess = !!sessionId;

  const { data: feeRates } = useQuery<Record<string, number>>({
    queryKey: ["/api/platform-fees/rates"],
  });

  const { data: wallet, isLoading } = useQuery<PublicWallet>({
    queryKey: ["/api/wallet/share", token],
    queryFn: async () => {
      const response = await fetch(`/api/wallet/share/${token}`);
      if (!response.ok) throw new Error("Wallet not found");
      return response.json();
    },
    enabled: !!token,
  });

  const verifyMutation = useMutation({
    mutationFn: async (sessionId: string) => {
      const response = await apiRequest("POST", "/api/wallet/contribute/verify", { sessionId });
      return response.json();
    },
  });

  const oneTimeCheckoutMutation = useMutation({
    mutationFn: async (data: ContributionFormValues) => {
      const response = await apiRequest("POST", "/api/wallet/contribute/checkout", {
        shareToken: token,
        amount: data.amount,
        contributorName: data.contributorName || "Anonymous",
        contributorEmail: data.contributorEmail || "",
      });
      return response.json();
    },
    onSuccess: (data) => {
      if (data.url) {
        window.location.href = data.url;
      }
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to create checkout session. Please try again.",
        variant: "destructive",
      });
    },
  });

  const subscriptionCheckoutMutation = useMutation({
    mutationFn: async (data: ContributionFormValues) => {
      const response = await apiRequest("POST", "/api/wallet/subscribe/checkout", {
        shareToken: token,
        monthlyAmount: data.amount,
        contributorName: data.contributorName || "Anonymous",
        contributorEmail: data.contributorEmail || "",
      });
      return response.json();
    },
    onSuccess: (data) => {
      if (data.url) {
        window.location.href = data.url;
      }
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to create subscription checkout. Please try again.",
        variant: "destructive",
      });
    },
  });

  const form = useForm<ContributionFormValues>({
    mode: 'onBlur',
    resolver: zodResolver(contributionSchema),
    defaultValues: {
      amount: 0,
      contributorName: "",
      contributorEmail: "",
    },
  });

  const handleAmountSelect = (amount: number) => {
    setSelectedAmount(amount);
    form.setValue("amount", amount);
  };

  const handleSubmit = (data: ContributionFormValues) => {
    if (contributionType === "one-time") {
      oneTimeCheckoutMutation.mutate(data);
    } else {
      subscriptionCheckoutMutation.mutate(data);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-background to-muted/30 py-12">
        <div className="container mx-auto px-4 max-w-2xl">
          <Skeleton className="h-8 w-48 mb-4" />
          <Skeleton className="h-64 w-full" />
        </div>
      </div>
    );
  }

  if (!wallet) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-background to-muted/30 py-12">
        <div className="container mx-auto px-4 max-w-2xl">
          <Card className="text-center py-12">
            <CardContent>
              <Heart className="h-16 w-16 mx-auto mb-4 text-muted-foreground opacity-50" />
              <h1 className="text-2xl font-bold mb-2">Wallet Not Found</h1>
              <p className="text-muted-foreground mb-6">
                This contribution link may be invalid or expired.
              </p>
              <Button variant="outline" onClick={() => setLocation("/")}>
                <ArrowLeft className="h-4 w-4 mr-2" />
                Return Home
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (isSuccess) {
    if (sessionId && !verifyMutation.isSuccess && !verifyMutation.isError) {
      verifyMutation.mutate(sessionId);
    }

    return (
      <div className="min-h-screen bg-gradient-to-b from-background to-muted/30 py-12">
        <div className="container mx-auto px-4 max-w-2xl">
          <Card className="text-center py-12">
            <CardContent className="space-y-6">
              <div className="flex items-center justify-center w-20 h-20 rounded-full bg-green-100 dark:bg-green-900/20 mx-auto">
                <CheckCircle className="h-10 w-10 text-green-600" />
              </div>
              <div>
                <h1 className="text-2xl font-bold mb-2" data-testid="text-success-title">
                  Thank You for Your Contribution!
                </h1>
                <p className="text-muted-foreground">
                  Your support will help {wallet.patientName} with their healthcare costs.
                </p>
              </div>

              {verifyMutation.isPending && (
                <div className="flex items-center justify-center gap-2 text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span>Processing your contribution...</span>
                </div>
              )}

              {verifyMutation.isSuccess && (
                <div className="p-4 rounded-lg bg-green-50 dark:bg-green-900/10 border border-green-200 dark:border-green-800">
                  <p className="text-green-700 dark:text-green-400 font-medium">
                    Contribution successfully added to the wallet!
                  </p>
                </div>
              )}

              <Separator />

              <div>
                <p className="text-sm text-muted-foreground mb-4">
                  Want to support {wallet.patientName} even more?
                </p>
                <Button
                  variant="outline"
                  onClick={() => {
                    const url = new URL(window.location.href);
                    url.search = "";
                    window.history.replaceState({}, "", url);
                    window.location.reload();
                  }}
                  data-testid="button-contribute-again"
                >
                  Make Another Contribution
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  const goalProgress = wallet.goalAmount
    ? Math.min((wallet.totalContributions / wallet.goalAmount) * 100, 100)
    : 0;

  const isPending = oneTimeCheckoutMutation.isPending || subscriptionCheckoutMutation.isPending;

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-muted/30 py-12">
      <div className="container mx-auto px-4 max-w-2xl space-y-8">
        <div className="text-center space-y-4">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 mb-2">
            <Heart className="h-8 w-8 text-primary" />
          </div>
          <h1 className="text-3xl font-bold" data-testid="text-wallet-name">
            Support {wallet.patientName}
          </h1>
          <Badge variant="secondary" className="text-sm">
            {getCategoryLabel(wallet.category)}
          </Badge>
          {wallet.description && (
            <p className="text-muted-foreground max-w-md mx-auto">{wallet.description}</p>
          )}
        </div>

        {wallet.goalAmount && (
          <Card>
            <CardContent className="pt-6">
              <div className="space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="font-medium">Funding Progress</span>
                  <span className="text-muted-foreground">
                    {formatCents(wallet.totalContributions)} of {formatCents(wallet.goalAmount)}
                  </span>
                </div>
                <Progress value={goalProgress} className="h-3" />
                <p className="text-sm text-muted-foreground text-center">
                  {goalProgress.toFixed(1)}% of goal reached
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CreditCard className="h-5 w-5" />
              Make a Contribution
            </CardTitle>
            <CardDescription>
              Every contribution helps {wallet.patientName} with their healthcare costs.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Tabs
              defaultValue="one-time"
              onValueChange={(v) => setContributionType(v as "one-time" | "monthly")}
            >
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="one-time" data-testid="tab-one-time">
                  <DollarSign className="h-4 w-4 mr-2" />
                  One-Time
                </TabsTrigger>
                <TabsTrigger value="monthly" data-testid="tab-monthly">
                  <Calendar className="h-4 w-4 mr-2" />
                  Monthly
                </TabsTrigger>
              </TabsList>

              <div className="mt-6">
                <Form {...form}>
                  <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
                    <div className="space-y-3">
                      <FormLabel>
                        {contributionType === "one-time" ? "Contribution Amount" : "Monthly Amount"}
                      </FormLabel>
                      <div className="grid grid-cols-5 gap-2">
                        {presetAmounts.map((amount) => (
                          <Button
                            key={amount}
                            type="button"
                            variant={selectedAmount === amount ? "default" : "outline"}
                            onClick={() => handleAmountSelect(amount)}
                            className="text-sm"
                            data-testid={`button-amount-${amount}`}
                          >
                            {formatCents(amount)}
                          </Button>
                        ))}
                      </div>
                      <FormField
                        control={form.control}
                        name="amount"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="sr-only">Custom Amount</FormLabel>
                            <FormControl>
                              <div className="relative">
                                <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                <Input
                                  type="number"
                                  step="0.01"
                                  min="1"
                                  placeholder="Or enter custom amount"
                                  className="pl-10"
                                  onChange={(e) => {
                                    const value = parseFloat(e.target.value);
                                    if (!isNaN(value)) {
                                      const cents = Math.round(value * 100);
                                      field.onChange(cents);
                                      setSelectedAmount(cents);
                                    }
                                  }}
                                  data-testid="input-custom-amount"
                                />
                              </div>
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>

                    <Separator />

                    <div className="space-y-4">
                      <FormField
                        control={form.control}
                        name="contributorName"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Your Name (Optional)</FormLabel>
                            <FormControl>
                              <div className="relative">
                                <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                <Input
                                  placeholder="Anonymous"
                                  className="pl-10"
                                  {...field}
                                  data-testid="input-contributor-name"
                                />
                              </div>
                            </FormControl>
                            <FormDescription>
                              Leave blank to contribute anonymously.
                            </FormDescription>
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="contributorEmail"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Email (Optional)</FormLabel>
                            <FormControl>
                              <div className="relative">
                                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                <Input
                                  type="email"
                                  placeholder="your@email.com"
                                  className="pl-10"
                                  {...field}
                                  data-testid="input-contributor-email"
                                />
                              </div>
                            </FormControl>
                            <FormDescription>For receipt and updates.</FormDescription>
                          </FormItem>
                        )}
                      />
                    </div>

                    {(() => {
                      const currentAmount = form.watch("amount");
                      if (currentAmount && currentAmount >= 100) {
                        const feeRate = contributionType === "one-time" 
                          ? (feeRates?.wallet_contribution ?? 3.0) 
                          : (feeRates?.wallet_subscription ?? 2.5);
                        const feeAmountCents = Math.round(currentAmount * (feeRate / 100));
                        const netAmountCents = currentAmount - feeAmountCents;
                        return (
                          <div className="p-3 rounded-md bg-muted/50 text-sm space-y-1" data-testid="fee-breakdown">
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">Your {contributionType === "one-time" ? "contribution" : "monthly amount"}</span>
                              <span>{formatCents(currentAmount)}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">Platform service fee ({feeRate}%)</span>
                              <span>-{formatCents(feeAmountCents)}</span>
                            </div>
                            <Separator />
                            <div className="flex justify-between font-medium">
                              <span>Amount going to {wallet.patientName}</span>
                              <span>{formatCents(netAmountCents)}</span>
                            </div>
                          </div>
                        );
                      }
                      return null;
                    })()}

                    <Button
                      type="submit"
                      className="w-full"
                      size="lg"
                      disabled={isPending || !form.getValues("amount")}
                      data-testid="button-submit-contribution"
                    >
                      {isPending ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Processing...
                        </>
                      ) : (
                        <>
                          <Heart className="h-4 w-4 mr-2" />
                          {contributionType === "one-time"
                            ? `Contribute ${form.getValues("amount") ? formatCents(form.getValues("amount")) : ""}`
                            : `Subscribe ${form.getValues("amount") ? formatCents(form.getValues("amount")) : ""}/month`}
                        </>
                      )}
                    </Button>
                  </form>
                </Form>
              </div>
            </Tabs>
          </CardContent>
          <CardFooter className="flex flex-col text-center text-sm text-muted-foreground space-y-2">
            <p>Secure payment processing by Stripe.</p>
            <p>A small platform service fee supports HealthWallet's operations and community infrastructure.</p>
            <div className="p-3 rounded-md bg-muted/50 text-xs space-y-1" data-testid="text-contribution-tax-disclosure">
              <p>Contributions are voluntary financial support processed by Stripe, Inc. HealthWallet does not hold or custody funds. Contributions may not be tax-deductible. Contributors should consult a tax professional regarding any tax implications.</p>
              <p>By contributing, you agree to our <a href="/terms" className="underline" target="_blank" rel="noopener noreferrer">Terms of Service</a> and <a href="/privacy" className="underline" target="_blank" rel="noopener noreferrer">Privacy Policy</a>.</p>
            </div>
          </CardFooter>
        </Card>

        <div className="text-center text-sm text-muted-foreground">
          <p>
            Powered by{" "}
            <span className="font-semibold text-foreground">HealthWallet</span>
          </p>
        </div>
      </div>
    </div>
  );
}
